import { RouteItem } from '@/app/model/route';
import type { TFunction } from 'i18next';
import { pick } from 'lodash';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';
import { routes } from '@/app/config/routes';
import { Tree } from '@/shared/lib/utils/tree/types';

interface Props {
  selectProperties: Array<keyof RouteItem>;
  t?: TFunction;
  flat?: boolean;
}

export const flatRoutes =
  <T extends {}>(childrenKey: keyof T) =>
  (transformer: (val: Omit<T, keyof T>) => any) =>
  (arr: Array<any>) => {
    const inner = (arr: T[] = []): Partial<RouteItem>[] =>
      arr.reduce((acc, curr) => {
        const { [childrenKey]: children, ...rest } = curr;
        acc.push(transformer(rest));
        if (children) acc = [...acc, ...inner(children as unknown as T[])];
        return acc;
      }, []);
    return inner(arr);
  };

export const getRoutes = ({
  selectProperties,
  flat,
}: Props): Partial<RouteItem>[] | Tree<Partial<RouteItem>> => {
  if (flat) {
    return flatRoutes<RouteItem>('children')((x: RouteItem) => ({
      ...x,
    }))(routes).map((x) => pick(x, selectProperties)) as Partial<RouteItem>[];
  }

  return treeMap(routes, 'children', (x) => pick(x, selectProperties)) as Tree<
    Partial<RouteItem>
  >;
};